/**
  * @class meleCalendarPlayer
  * @description
  *
  * # Purpose
  * Implements `<mele-calendar-player>` custom web element for showing calendar for player
  */

import msysListener from 'msysListener'
import meleElement from 'meleElement'

const PROPERTIES = _.extend({}, {
  listener_id: { type: 'string', description: 'desired listener_id for the streaming listener; if not given, automatic numeric ID will be assigned' },
  value: { type: 'string', description: 'current selected time in epoch seconds' },
  unit: { type: 'string', default: 'day', enum: ['year', 'month', 'day', 'hour', 'minute'], description: 'currently selected unit' },
  source: { type: 'string', description: 'source of streaming; empty string means our application, @ means peering, Bxxxxx: means remote machine', default: 'SERVER@msysStreamer' },
  indexes: { type: 'array', description: 'list of indexes to be retrieved for styling time cells', default: ['h264', 'markered', 'lightbared', 'sirened'] },
  eventValueChanged: { type: 'function', description: 'Called when user clicks on any unit element' },
}, meleElement._properties)

export default class meleCalendarPlayer extends meleElement {
  init() {
    this.innerHTML = `
      <unit></unit>
      <values></values>
    `
    this.elUnit = this.find('unit')
    this.elValues = this.find('values')
    this.elValues.addEventListener('click', this.valueClicked.bind(this))

    super.init()
    if(this.getAttribute('unit')===null) this.setAttribute('unit', this.unit)

    setTimeout(()=>this.refresh(), 10) // wait a bit to get listener_id attribute
  } // init

  unitChanged() {
    this.refresh()
  } // unitChanged

  sourceChanged() {
    this.refresh()
  } // sourceChanged

  indexesChanged() {
    this.refresh()
  } // indexesChanged

  async valueChanged() {
    this.refresh()
  } // valueChanged

  refresh() {
    const unit = this.unit
    this.elUnit.innerHTML = this.translate('calendar.'+unit)
    const fcn = this['refresh'+_.capitalize(String(this.unit||'day'))]
    fcn.call(this)
  } // refresh

  async refreshYear() {
    const dt = new Date(this.value*1000)
    const value = dt.getFullYear()
    const year = new Date().getFullYear()
    const min_year = value<year ? value-2 : year-2
    const years = year - min_year + 1
    this.elValues.innerHTML = Array.from({ length: years }, (x, i)=>`<v ${min_year+i===value?'selected':''}>${_.two(min_year+i)}</v>`).join('')
  }

  refreshMonth() {
    const dt = new Date(this.value*1000)
    const value = dt.getMonth()
    this.elValues.innerHTML = Array.from({ length: 12 }, (x, i)=>`<v ${i===value?'selected':''}>${_.two(i+1)}</v>`).join('')
  }

  refreshDay() {
    const dt = new Date(this.value*1000)
    const days = new Date(dt.getFullYear(), dt.getMonth() + 1, 0).getDate()
    const value = dt.getDate()
    this.elValues.innerHTML = Array.from({ length: days }, (x, i)=>`<v ${i+1===value?'selected':''}>${_.two(i+1)}</v>`).join('')
    dt.setDate(1)
    const start = Math.floor(dt.valueOf()/1000/60/60/24)
    const end = start + days - 1
    this.refreshStyleUsingIndexes(start, end)
  }

  async refreshHour() {
    const dt = new Date(this.value*1000)
    const value = dt.getHours()
    this.elValues.innerHTML = Array.from({ length: 24 }, (x, i)=>`<v ${i===value?'selected':''}>${_.two(i)}</v>`).join('')
    dt.setHours(0)
    const start = Math.floor(dt.valueOf()/1000/60/60)
    const end = start + 23
    this.refreshStyleUsingIndexes(start, end)
  }

  refreshMinute() {
    const dt = new Date(this.value*1000)
    const value = dt.getMinutes()
    this.elValues.innerHTML = Array.from({ length: 60 }, (x, i)=>`<v ${i===value?'selected':''}>${_.two(i)}</v>`).join('')
    dt.setMinutes(0)
    const start = Math.floor(dt.valueOf()/1000/60)
    const end = start + 59
    this.refreshStyleUsingIndexes(start, end)
  }

  refreshSecond() {
    const dt = new Date(this.value*1000)
    const value = dt.getSeconds()
    this.elValues.innerHTML = Array.from({ length: 60 }, (x, i)=>`<v ${i===value?'selected':''}>${_.two(i)}</v>`).join('')
    dt.setSeconds(0)
    const start = Math.floor(dt.valueOf()/1000)
    const end = start + 59
    this.refreshStyleUsingIndexes(start, end)
  }

  async refreshStyleUsingIndexes(start, end) {
    if(!(start>0)) return

    if(!this.listener) this.listener = new msysListener(this.listener_id || 0)
    if(!this.listener.listener_id) {
      // creation of listener failed as there is already one running
      this.listener = this.listener._listeners[this.listener_id]?.ref
      if(!this.listener) return
    }

    const current_value = this.value
    const current_unit = this.unit
    for(const index of this.indexes) {
      try{
        const { params: { list } } = await this.listener.request(this.source, 'mmGetIndexes', { index, start, end, mode: 'list', level: current_unit }, { resolve: 'mmIndexes', timeout: 2000 })
        if(!this.isConnected || current_value!==this.value || current_unit!==this.unit) return // calendar already changed
        for(const idx of list) {
          this.elValues.children[idx].classList.add(index)
          const el = document.createElement('idx')
          el.className = index
          this.elValues.children[idx].append(el)
        }
      }catch(e) {
        if(!this.isConnected || current_value!==this.value || current_unit!==this.unit) return // calendar already changed
        console.error(e)
      }
      if(!this.isConnected || current_value!==this.value || current_unit!==this.unit) return // calendar already changed
    }
  }

  disconnectedCallback() {
    if(this.listener) {
      this.listener.mmExit(null, { delete: true })
      delete this.listener
    }
  } // disconnectedCallback

  valueClicked(e) {
    const old = this.value
    const value = parseInt(e.target.innerHTML)
    if(isNaN(value)) return
    const unit = this.unit
    const dt = new Date(this.value*1000)
    if(unit==='year') {
      const old_month = dt.getMonth()
      dt.setFullYear(value)
      if(dt.getMonth()!==old_month) dt.setDate(0)
    }else if(unit==='month') {
      dt.setMonth(value-1)
      if(dt.getMonth()!==value-1) dt.setDate(0)
    }else if(unit==='day') {
      dt.setDate(value)
    }else if(unit==='hour') {
      dt.setHours(value)
    }else if(unit==='minute') {
      dt.setMinutes(value)
    }else if(unit==='second') {
      dt.setSeconds(value)
    }
    this.value = Math.floor(dt.valueOf()/1000)
    if(this.eventValueChanged) this.eventValueChanged(this, this.value, old, true)
  } // valueClicked
} // class meleCalendarPlayer

meleCalendarPlayer._properties = PROPERTIES
meleCalendarPlayer.observedAttributes = _.keys(PROPERTIES)
window.customElements.define('mele-calendar-player', meleCalendarPlayer)
